import { useTranslation } from "@i18n";
import { useCallback, useMemo, useRef, useState } from "react";
import { estimateItemsUrl } from "@Services/dataManager";
import {
  correctDecimalPlaces,
  ESTIMATE_LEVEL_MEASUREMENT_UNIT,
  isEstimateRequiredPermission,
} from "@Actions/estimation";
import EstimationDetails from "./EstimationDetails";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import SlickGrid1, { withDynamicColumns, type SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import { renderToStaticMarkup } from "react-dom/server";
import { HeaderWithIcon } from "@CubeComponents/html/SlickGrid1/Formatters/Formatters";
import { folderPermissions } from "@Helpers/constants";
import { RootState } from "@Reducers/index";
import type { EstimateCurrency, EstimateData as EstimateDataState } from "@ServerTypes/estimates/estimates.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

const { RESOURCE_MODIFY } = folderPermissions();

// ADDED: `isSystemGenerated` is read from `takeoffData` redux slice but isn't part of the
// shared TakeoffData schema. Tracked as a frontend-only view flag.
type TakeoffDataState = {
  isSystemGenerated?: boolean;
};

type PurchaseUnit = {
  id?: string;
  name?: string;
};

type EstimateQuantity = {
  estimateVersionId: string;
  quantity: number;
  amount: number;
};

// ADDED: ItemRecord shape reflects the EstimateItems projection used in the grid
// (server schema is currently z.record(z.string(), z.unknown())). `__isDummy` is
// a frontend SlickGrid placeholder marker.
type ItemRecord = {
  _id?: string;
  /** EstimateItems business key — `objectIdString` in estimates.schema.ts (fed to `new ObjectId(...)`). */
  itemId: ObjectIdString;
  code?: string;
  description?: string;
  purchaseUnit?: PurchaseUnit;
  rate?: number;
  amount?: number;
  additionalAmount?: number;
  manualQuantity?: number;
  quantityFromTakeoffs?: number;
  estimateQuantity?: EstimateQuantity[];
  __isDummy?: boolean;
};

/** The BOQ sidebar's view of a row: quantity/rate are editable text while the sidebar is open. */
type ItemDetailsState = Omit<ItemRecord, "manualQuantity" | "rate"> & {
  manualQuantity?: number | string;
  rate?: number | string;
};

type ItemsGridProps = {
  estimateId: ObjectIdString;
  currencyData: EstimateCurrency;
  costDatabaseVersionId?: ObjectIdString;
  takeOffItemId?: string;
  takeOffAttachedItems?: boolean;
  /** Takeoff-scoped: the Delete/Duplicate toolbar entries that invoke these are only built when
   *  `takeOffItemId && takeOffAttachedItems`, so a host without a takeoff context (the Estimate
   *  page's Items tab) has nothing to build them from. Optional, and guarded at both call sites. */
  removeItemsFromTakeoffs?: (payload: { itemIds: ObjectIdString[] }) => Promise<boolean>;
  duplicateItem?: (payload: { itemId: ObjectIdString }) => Promise<void>;
  constituentEstimateVersionDetails?: Array<{ versionId: string; counter: number }>;
  selectingEvent?: (args: ItemRecord[]) => void;
};

type QueryType = {
  estimateId: ObjectIdString;
  getChildrenOnly: boolean;
  versionId?: string;
  costDatabaseVersionId?: ObjectIdString;
  params: {
    estimateId: ObjectIdString;
    getChildrenOnly: boolean;
    versionId?: string;
    costDatabaseVersionId?: ObjectIdString;
    takeOffAttachedItems?: boolean;
    takeOffItemId?: string;
  };
  takeOffAttachedItems?: boolean;
  takeOffItemId?: string;
};

const ItemsGrid = ({
  estimateId,
  currencyData,
  costDatabaseVersionId,
  takeOffItemId,
  takeOffAttachedItems,
  removeItemsFromTakeoffs,
  duplicateItem,
  constituentEstimateVersionDetails,
  selectingEvent,
}: ItemsGridProps) => {
  const { t } = useTranslation();
  const dataGrid = useRef<SlickGrid1Ref<ItemRecord> | null>(null);
  const params = useParams<{ versionId?: string }>();
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataState;
  const takeoffData = useSelector((state: RootState) => state.takeoffData) as TakeoffDataState;
  const urlSearchParams = new URLSearchParams(location.search);
  const versionId = params.versionId || urlSearchParams.get("versionId");
  const actionPermissionAllowed = isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id);

  const [showSidebar, setShowSidebar] = useState(false);
  // `null` while nothing is selected. The previous placeholder row stood in for that with an empty
  // `_id`, but it carried no `itemId` either, so it was never actually an `ItemRecord`.
  const [itemDetails, setItemDetails] = useState<ItemDetailsState | null>(null);

  const getQueryInitialState = () => {
    let data: QueryType = {
      estimateId: estimateId,
      getChildrenOnly: true,
      versionId: params.versionId,
      costDatabaseVersionId,
      params: {
        estimateId: estimateId,
        getChildrenOnly: true,
        versionId: params.versionId,
        costDatabaseVersionId,
      },
    };
    if (takeOffItemId) {
      data.takeOffAttachedItems = takeOffAttachedItems;
      data.takeOffItemId = takeOffItemId;
      data.params.takeOffAttachedItems = takeOffAttachedItems;
      data.params.takeOffItemId = takeOffItemId;
    }

    return data;
  };

  const [query] = useState(getQueryInitialState());
  const [enableWrapText, setEnableWrapText] = useState(false);
  const [toolbarButtonDisableState, setToolbarButtonDisableState] = useState({
    duplicate: true,
    delete: true,
    properties: true,
  });

  const toolbarOptions = useMemo(() => {
    if (!versionId && takeOffItemId && takeOffAttachedItems && !takeoffData.isSystemGenerated) {
      return [
        {
          id: "duplicate",
          name: t("Duplicate"),
          prefixIcon: "Duplicate",
          disabled: toolbarButtonDisableState.duplicate,
        },
        {
          id: "delete",
          name: t("Remove"),
          prefixIcon: "delete",
          disabled: toolbarButtonDisableState.delete,
        },
      ];
    }
    return [] as Array<{ id: string; name: string; prefixIcon: string; disabled: boolean }>;
  }, [versionId, takeOffItemId, takeOffAttachedItems, takeoffData.isSystemGenerated, toolbarButtonDisableState]);

  const refreshDataGrid = useCallback(() => {
    dataGrid.current?.refresh?.();
  }, []);

  const openSidebar = useCallback((itemData: ItemRecord) => {
    setShowSidebar(true);
    const quantity: number | string =
      itemData.rate === 0 || !itemData.purchaseUnit?.id ? "" : itemData.manualQuantity ? itemData.manualQuantity : 0;
    setItemDetails({
      ...itemData,
      manualQuantity: quantity,
      rate: itemData.rate === 0 ? "" : itemData.rate,
    });
  }, []);

  const toolbarClickHandler = useCallback(
    async (toolClicked: string) => {
      if (dataGrid.current) {
        // @slickgrid-universal/common declares `getAllSelectedItems(): T[]`, but its implementation
        // pushes `getItemById(id)` for every id still in `selectedRowIds` — that is `undefined` for a
        // selection kept across a refresh/filter, so the holes below are real. Widened on assignment
        // (never asserted) to keep the guard honest.
        const selectedItems: Array<ItemRecord | undefined> = dataGrid.current.dataView.getAllSelectedItems();
        const selectedRecords = selectedItems.filter(
          (record): record is ItemRecord => record != null && !record.__isDummy,
        );
        let refreshdata = false;
        if (toolClicked == "delete" && removeItemsFromTakeoffs) {
          setToolbarButtonDisableState((prev) => ({ ...prev, delete: true, duplicate: true }));
          await removeItemsFromTakeoffs({ itemIds: selectedRecords.map((record: ItemRecord) => record.itemId) });
          setToolbarButtonDisableState((prev) => ({ ...prev, delete: false, duplicate: false }));
          refreshdata = true;
        } else if (toolClicked == "duplicate" && duplicateItem) {
          if (selectedRecords.length == 1 && selectedRecords[0]) {
            setToolbarButtonDisableState((prev) => ({ ...prev, delete: true, duplicate: true }));
            await duplicateItem({ itemId: selectedRecords[0].itemId });
            setToolbarButtonDisableState((prev) => ({ ...prev, delete: false, duplicate: false }));
            refreshdata = true;
          }
        } else {
          if (selectedRecords.length == 1 && selectedRecords[0]) {
            openSidebar(selectedRecords[0]);
          }
        }

        if (refreshdata) {
          refreshDataGrid();
        }
      }
    },
    [removeItemsFromTakeoffs, duplicateItem, openSidebar, refreshDataGrid],
  );

  const selectionEvent = () => {
    if (dataGrid.current) {
      // Same `getAllSelectedItems()` hole as in `toolbarClickHandler` above — widened on assignment.
      const selectedItems: Array<ItemRecord | undefined> = dataGrid.current.dataView.getAllSelectedItems();
      const selectedRecords = selectedItems.filter(
        (record): record is ItemRecord => record != null && !record.__isDummy,
      );
      if (selectingEvent) {
        selectingEvent(selectedRecords);
      } else {
        if (takeOffItemId && takeOffAttachedItems && !versionId && actionPermissionAllowed) {
          setToolbarButtonDisableState((prev) => ({
            ...prev,
            duplicate: selectedRecords.length !== 1,
            delete: selectedRecords.length == 0,
          }));
        } else if (!takeOffItemId) {
          setToolbarButtonDisableState((prev) => ({
            ...prev,
            properties: selectedRecords.length !== 1,
          }));
        }
      }
    }
  };

  const closeSidebar = () => {
    setShowSidebar(false);
    setItemDetails(null);
  };

  const quantityTemplate = (params: ItemRecord, key?: string) => {
    // Both branches assign, so no placeholder initialiser.
    let totalQuantity: number | null;
    if (params.estimateQuantity && params.estimateQuantity.length) {
      const matchingItem = params.estimateQuantity.find((item: EstimateQuantity) => item.estimateVersionId === key);
      totalQuantity = matchingItem ? matchingItem.quantity : null;
    } else {
      totalQuantity = (params.manualQuantity ?? 0) + (params.quantityFromTakeoffs ?? 0);
    }
    return totalQuantity == null || params.rate === 0 || !params.purchaseUnit?.id
      ? "-"
      : correctDecimalPlaces({ value: totalQuantity, decimals: 2 });
  };

  const amountTemplate = (params: ItemRecord) => {
    // Both branches assign before the first read, so no placeholder initialiser.
    let totalAmount: number | undefined;
    if (params.estimateQuantity && params.estimateQuantity.length) {
      totalAmount = params.estimateQuantity.reduce(
        (accumulator: number, currentValue: EstimateQuantity) => accumulator + currentValue.amount,
        0,
      );
      if (params.additionalAmount) {
        totalAmount = (totalAmount || 0) + params.additionalAmount;
      }
    } else {
      totalAmount = params.amount ?? undefined;
    }

    return totalAmount !== undefined ? correctDecimalPlaces({ value: totalAmount, decimals: 2 }) : "-";
  };

  const unitTemplate = (params: ItemRecord) => (params.purchaseUnit?.id ? params.purchaseUnit.name : "-");
  const rateTemplate = (params: ItemRecord) => (params.rate !== 0 ? params.rate : "-");

  // const columns = constituentEstimateVersionDetails?.map((item: any) => {
  //   const formattedCounter = `ES-${item.counter.toString().padStart(4, "0")}`;
  //   return {
  //     key: `${item.versionId}`,
  //     field: "quantity",
  //     headerText: formattedCounter,
  //     template: quantityTemplate,
  //     allowFiltering: false,
  //     allowSorting: false,
  //   };
  // });

  const columns = constituentEstimateVersionDetails?.map((item: { versionId: string; counter: number }) => {
    const formattedCounter = `ES-${item.counter.toString().padStart(4, "0")}`;
    return {
      field: "quantity",
      name: formattedCounter,
      id: "quantity",
      width: 150,
      originalWidth: 150,
      _valueGetter: (args: ItemRecord) => quantityTemplate(args, item.versionId),
      clipMode: "EllipsisWithTooltip",
      columnGroup: t("Quantity"),
      headerRenderer: () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={formattedCounter} />),
    };
  });

  const codeHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Code")} />);
  const descriptionHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Description")} />);
  const rateHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Rate")} />);
  const purchaseUnitRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Dropdown" label={t("Unit")} />);
  const amountRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Amount")} />);
  const quantityRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Total Quantity")} />);

  const [columnDefinitions] = useState<
    Array<{
      id: string;
      field: string;
      name: string;
      originalWidth?: number;
      width?: number;
      filterType?: string;
      clipMode?: string;
      _valueGetter?: (args: ItemRecord) => unknown;
      headerRenderer?: () => string;
      columnGroup?: string;
    }>
  >([
    {
      id: "code",
      field: "code",
      name: t("Code"),
      originalWidth: 180,
      width: 120,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
      headerRenderer: codeHeaderRenderer,
    },
    {
      id: "description",
      field: "description",
      name: t("Description"),
      originalWidth: 180,
      width: 280,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
      headerRenderer: descriptionHeaderRenderer,
    },
    {
      id: "rate",
      field: "rate",
      name: t("Rate"),
      originalWidth: 180,
      width: 280,
      clipMode: "EllipsisWithTooltip",
      _valueGetter: (args: ItemRecord) => rateTemplate(args),
      headerRenderer: rateHeaderRenderer,
    },
    {
      id: "purchaseUnit",
      field: "params.purchaseUnit.name",
      name: t("Unit"),
      originalWidth: 180,
      width: 280,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
      _valueGetter: (args: ItemRecord) => unitTemplate(args),
      headerRenderer: purchaseUnitRenderer,
    },
    ...(takeOffItemId
      ? []
      : [
          {
            id: "amount",
            field: "amount",
            name: t("Amount"),
            originalWidth: 180,
            width: 280,
            clipMode: "EllipsisWithTooltip",
            _valueGetter: (args: ItemRecord) => amountTemplate(args),
            headerRenderer: amountRenderer,
          },
        ]),
    ...(columns && columns.length > 0
      ? columns
      : [
          {
            id: "quantity",
            field: "quantity",
            name: t("Total Quantity"),
            originalWidth: 180,
            width: 280,
            filterType: "Number",
            clipMode: "EllipsisWithTooltip",
            _valueGetter: (args: ItemRecord) => quantityTemplate(args),
            headerRenderer: quantityRenderer,
          },
        ]),
  ]);

  const itemsGrid = () => (
    <div className="flex w-full h-full">
      <SlickGrid1
        fitColumnWidthToHeader
        ref={dataGrid}
        // `withDynamicColumns` rather than `typedColumns`: the per-version quantity columns are
        // built at runtime from `constituentEstimateVersionDetails`, and the unit column addresses
        // a nested value by path ("params.purchaseUnit.name") — neither can be a `keyof ItemRecord`.
        columnDefinitions={withDynamicColumns<ItemRecord>(columnDefinitions)}
        gridOptions={{
          enableCheckboxSelector: !params.versionId,
          enableSelection: true,
          headerRowHeight: 200,
          showPreHeaderPanel: true,
          preHeaderPanelHeight: 30,
          topHeaderPanelHeight: 30,
          showHeaderRow: false,
        }}
        toolbar={toolbarOptions}
        enableRowResize={true}
        remoteDataSource={estimateItemsUrl}
        query={query}
        enableSelectAllCheckbox={true}
        onSelectedRowIdsChanged={selectionEvent}
        allowPagination={false}
        toolbarClickHandler={toolbarClickHandler}
        propertiesPanel={!takeOffItemId}
        propertiesDisabled={toolbarButtonDisableState.properties}
        enableFilteringOption={true}
        enableSortingOption={true}
        enableColumnChooser={!takeOffItemId}
        enableWrapText={enableWrapText}
        setEnableWrapText={setEnableWrapText}
        enableGroupingOption
        excelExport
        hideNoDataIllustration
      />
    </div>
  );

  return (
    <div className="flex flex-col overflow-auto h-full">
      {showSidebar && itemDetails?._id && (
        <EstimationDetails
          closeSidebar={closeSidebar}
          data={itemDetails}
          source={ESTIMATE_LEVEL_MEASUREMENT_UNIT}
          sourceId={estimateId}
          costDatabaseVersionId={costDatabaseVersionId}
          currencyData={currencyData}
          showAnalysisOption={false}
          refreshDataGrid={refreshDataGrid}
        />
      )}
      <div className="h-full overflow-auto">{itemsGrid()}</div>
    </div>
  );
};
ItemsGrid.displayName = "ItemsGrid";
export default ItemsGrid;
